import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { MediaRequests, OrganizationEvents } from "@app/shared/src/api/api.gen";
import { getApiUrl, UserError } from "@app/shared/src/api/api_client";
import {
    EventTrigger,
    EventTriggerType,
    FacilityEvent,
    MediaRequestStatuses,
    TargetHistory,
    getTargetTypeDisplayName,
} from "@app/shared/src/models";
import {
    Btn,
    Card,
    CardHead,
    FRow,
    FVal,
    LineAreaChart,
    MPS_TO_MPH,
    Note,
    OmnisightMark,
    SettingsIcon,
    Spinner,
    Tag,
    Table,
    Td,
    Th,
    TipRow,
    TipTitle,
    useToast,
} from "@app/shared/src/components_v2";
import { sideOfZoneName } from "@app/shared/src/helpers/zoneNameHelpers";
import { BackLink } from "../../helpers/backLink";
import { siteLabel } from "../../helpers/deviceLabels";
import { CATEGORY_LABEL, categorise, eventTimeToCollisionMs, formatMs, isReviewed, severityTone } from "../../helpers/eventDisplay";
import { isMediaRequestWorking } from "../../helpers/mediaRequestStatus";
import { MissingTrackReason, missingTrackClipNote, missingTrackNote, missingTrackReason } from "../../helpers/missingTrack";
import { useLoad } from "../../hooks/useLoad";
import { useZoneNames } from "../../hooks/useZoneNames";
import { useFleet } from "../providers/FleetProvider";
import { useOrg } from "../providers/OrgProvider";
import { MediaRequestStatusTag } from "../shared/MediaRequestStatusTag";
import { ReviewEventDialog } from "../shared/ReviewEventDialog";

/**
 * The work-zone incident report: one event, everything the server holds about it, printable.
 *
 * Seeds from the row the director clicked (router state) so the page paints instantly, then
 * fetches the event by id with its radar track to fill the path, speed profile and history table.
 * A shared link has no row to seed from and names no organization; the by-id lookup needs
 * neither. Every figure is the recorded track — nothing interpolated.
 */

const M_TO_FT = 3.28084;

/** How often a pull in progress re-reads itself. A clip off the DVR lands in a couple of minutes. */
const CLIP_POLL_MS = 15_000;

/** The server's one answer for a missing event and for someone else's — see the hydrate effect. */
const NOT_FOUND = "This event could not be found. It may have been removed, or it belongs to an organization you are not a member of.";

type ClipInfo = { status?: string; videoUrl?: string | null; photoUrl?: string | null };

export const EventReportPage: React.FC = () => {
    const { eventId } = useParams<{ eventId: string }>();
    const location = useLocation() as { state?: BackLink & { event?: FacilityEvent } };
    const { orgs, org, setOrgId } = useOrg();
    // Org-wide: a report can be opened for a truck the current group filter hides.
    const { allDevices, deviceName, loadTriggers } = useFleet();
    const zoneNames = useZoneNames();

    const [event, setEvent] = useState<FacilityEvent | undefined>(location.state?.event);
    /** Which organization the server found the event in — the one the review must be filed under. */
    const [eventOrgId, setEventOrgId] = useState<string>();
    const [hydrated, setHydrated] = useState(false);
    const [error, setError] = useState<string>();
    const [clip, setClip] = useState<ClipInfo>();
    const [trigger, setTrigger] = useState<EventTrigger>();
    const [reviewOpen, setReviewOpen] = useState(false);
    // Which radar fix the reader is pointing at. Owned here because the path and the speed profile
    // both read and write it — that shared index is what links the two charts.
    const [hoverFix, setHoverFix] = useState<number>();

    // Hydrate: fetch this event by id, with its radar track. The id is all a shared link carries,
    // so the server resolves the organization from the event — and says which, so the console can
    // follow it (below) instead of guessing from whichever organization happened to be selected.
    useEffect(() => {
        if (!eventId) return;
        let cancelled = false;
        (async () => {
            try {
                const result = await OrganizationEvents.getById(eventId);
                if (cancelled) return;
                if (result?.event) {
                    setEvent(result.event);
                    setEventOrgId(result.organizationId);
                    setError(undefined);
                } else {
                    setError(NOT_FOUND);
                }
            } catch (err) {
                if (cancelled) return;
                // A 404 is the server's answer for "no such event" and "not yours" alike: the id
                // alone must not confirm that someone else's event exists.
                const missing = err instanceof UserError && err.status === 404;
                setError(missing ? NOT_FOUND : err instanceof Error ? err.message : "Could not load the event");
            } finally {
                if (!cancelled) setHydrated(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [eventId]);

    // Follow the event to its organization. Every other page is scoped to the selected one, so a
    // report left under a different organization would hand "back" and the device lookups the
    // wrong fleet. The server only returns an event the reader may see, so its organization is in
    // their list — once that list has loaded.
    useEffect(() => {
        if (!eventOrgId || !org || eventOrgId === org.id) return;
        if (orgs.some((o) => o.id === eventOrgId)) setOrgId(eventOrgId);
    }, [eventOrgId, org, orgs, setOrgId]);

    // The clip rides the same anonymous clip-info endpoint the emailed links use.
    useEffect(() => {
        if (!event?.clipAccessToken) return;
        let cancelled = false;
        fetch(getApiUrl(`/api/events/clip-info?token=${encodeURIComponent(event.clipAccessToken)}`))
            .then((r) => r.json())
            .then((body) => {
                if (!cancelled) setClip(body?.data as ClipInfo);
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [event?.clipAccessToken]);

    // The trigger that owns this event's zone — its endpoints say what was configured to fire.
    useEffect(() => {
        if (!event || allDevices.length === 0) return;
        let cancelled = false;
        loadTriggers().then((byFacility) => {
            if (cancelled || !event.facilityId) return;
            const triggers = byFacility.get(event.facilityId) ?? [];
            const match =
                triggers.find((t) => event.detectionZoneId && t.detectionZone?.id === event.detectionZoneId) ??
                triggers.find((t) => t.triggerType === EventTriggerType.ImpendingZoneEntry && !sideOfZoneName(t.detectionZone?.name));
            setTrigger(match);
        });
        return () => {
            cancelled = true;
        };
    }, [event, allDevices, loadTriggers]);

    const zoneName = event?.detectionZoneId ? zoneNames.get(event.detectionZoneId) : undefined;
    const category = event ? categorise(event, zoneName) : "other";
    const device = allDevices.find((d) => d.id === event?.deviceId);

    const track = useMemo(() => {
        const history = event?.target?.history ?? [];
        return [...history]
            .filter((h) => h.position)
            .sort((a, b) => new Date(a.date ?? 0).getTime() - new Date(b.date ?? 0).getTime());
    }, [event]);

    const facts = useMemo(() => {
        const t = event?.target;
        const speeds = track.map((h) => Math.hypot(h.velocity?.x ?? 0, h.velocity?.y ?? 0, h.velocity?.z ?? 0));
        // Last-resort fallback only for events whose eventValue really is a speed. On an impending
        // zone entry it is milliseconds, and reading it as m/s put "615 mph" on the report.
        const valueAsSpeed = event && event.eventValue > 0 && eventTimeToCollisionMs(event) === undefined ? event.eventValue : undefined;
        const maxMps = t?.maximumVelocity ?? (speeds.length ? Math.max(...speeds) : valueAsSpeed);
        const avgMps = t?.averageVelocity ?? (speeds.length ? speeds.reduce((a, b) => a + b, 0) / speeds.length : undefined);
        const ranges = track.map((h) => Math.hypot(h.position?.x ?? 0, h.position?.y ?? 0));
        const closestM = ranges.length ? Math.min(...ranges) : undefined;
        return {
            max: maxMps !== undefined ? `${Math.round(Math.abs(maxMps) * MPS_TO_MPH)} mph` : "—",
            avg: avgMps !== undefined ? `${Math.round(Math.abs(avgMps) * MPS_TO_MPH)} mph` : "—",
            closest: closestM !== undefined ? `${Math.round(closestM * M_TO_FT)} ft` : "—",
            cls: t ? getTargetTypeDisplayName(t.type) ?? "—" : "—",
        };
    }, [event, track]);

    if (error && !event) return <Note tone="crit">{error}</Note>;
    if (!event) {
        return (
            <div className="grid place-items-center p-12 text-[var(--muted)]">
                <Spinner />
            </div>
        );
    }

    // Coming from a truck's page, "back" means that truck — not the fleet-wide log.
    const cameFrom = location.state?.from;
    const backTo = cameFrom ?? "/events";
    const backLabel = cameFrom && cameFrom.startsWith("/devices") ? `Back to ${siteLabel(device)}` : "All events";

    // What became of a track that isn't here, judged by when the unit was last heard from (see
    // missingTrack). Only once the refetch has answered: a fetch that failed says nothing about it.
    const trackReason: MissingTrackReason | undefined =
        hydrated && !error && track.length === 0 ? missingTrackReason(event, device) : undefined;

    /*
     * What a link out of this report hands the device pages, so their own back link lands here
     * again — and lands on a *working* report.
     *
     * `fromState` carries the event row back, so the report paints at once on the return trip
     * rather than after the lookup by id — which would find it either way.
     */
    const backToReport: BackLink = {
        from: `/events/${event.id}`,
        fromLabel: "Incident report",
        fromState: { event, from: cameFrom },
    };

    /*
     * The server hands back AttachmentUrl as a path rooted at the API — "/api/organization/…/photo"
     * (EventSnapshotHelper). An <img src> with that value resolves against whatever origin is
     * serving this app, and Fleet Commander is not the API: fleet.omnisightusa.com vs
     * api.omnisightusa.com. So the browser asked Fleet Commander for the photo, got a 404, and the
     * report showed no image at all.
     *
     * Generated API clients never hit this because they route through getApiUrl; a raw src attribute
     * bypasses it, so it has to be applied here. Same shape the dashboard's EventSnapshot uses.
     *
     * The clip's own photoUrl is left alone — that one is already an absolute signed storage URL,
     * and prefixing it would break a link that currently works.
     */
    const photoUrl = event.attachmentUrl ? getApiUrl(event.attachmentUrl) : clip?.photoUrl ?? undefined;

    /*
     * A clip exists if either route says so — the token the trigger stamped, or the media request
     * the server matched by target window.
     *
     * The two are played differently on purpose. The anonymous clip-info endpoint hands back a
     * signed storage URL, which a <video> element can load straight. A request resolved by window is
     * served by an endpoint behind the normal API auth, so it opens in a tab exactly the way the main
     * dashboard opens it rather than being inlined here on the assumption a media element would
     * authenticate.
     */
    /*
     * "Pulled" and "promised" are not the same claim, and the card must not blur them. A
     * clipRequestId means the server actually found a media request — footage was pulled. A token on
     * its own only means the trigger asked for video at event time; the unit may not have sent the
     * request yet, and if it never comes back it never will. Both still open the card, because the
     * reader wants to know either way, but the wording below has to tell them apart.
     */
    const clipPulled = Boolean(event.clipRequestId);
    const hasClip = Boolean(event.clipAccessToken) || clipPulled;
    const clipVideoUrl = clip?.videoUrl ?? undefined;
    const clipWatchUrl =
        !clipVideoUrl && event.hasClipVideo && event.clipRequestId && event.deviceId
            ? getApiUrl(`/api/mediarequest/${event.deviceId}/media-requests/${event.clipRequestId}/video/view`)
            : undefined;

    /*
     * "Pulled" and "playable" are separate questions and the card needs both. A matched media
     * request proves someone pulled footage; clipStatus is what says whether it survived. An
     * expired, cancelled or errored request is a pull that left nothing behind — telling that
     * reader the unit is "still uploading, check back shortly" is a promise the page can never
     * keep, and it would sit there forever.
     */
    const deadClipNote =
        event.clipStatus === MediaRequestStatuses.Expired
            ? "That clip has aged out of the unit's storage and is no longer available."
            : event.clipStatus === MediaRequestStatuses.Cancelled
              ? "The request for this footage was cancelled before it uploaded."
              : event.clipStatus === MediaRequestStatuses.Error
                ? "The unit could not produce this clip. Requesting it again may work."
                : undefined;
    const warningMs = eventTimeToCollisionMs(event);
    const when = event.dateCreated ? new Date(event.dateCreated) : undefined;
    const tone = severityTone(category);
    const emailEndpoints =
        trigger?.eventTriggerClientEndpoints
            ?.map((e) => e.clientEndpoint)
            .filter((e): e is NonNullable<typeof e> => Boolean(e))
            .filter((e) => (e as { emailAddress?: string }).emailAddress) ?? [];
    const relayEndpoints =
        trigger?.eventTriggerClientEndpoints
            ?.map((e) => e.clientEndpoint)
            .filter((e): e is NonNullable<typeof e> => Boolean(e))
            .filter((e) => (e as { $type?: string }).$type?.includes("WebRelay")) ?? [];

    return (
        <div className="flex flex-col gap-4" id="incident-report">
            <Link to={backTo} className="text-[12.5px] font-semibold text-[var(--muted)] print:hidden">
                ← {backLabel}
            </Link>

            {/* Report head */}
            <div className="flex flex-wrap items-center gap-5 border-b-2 border-[var(--line-2)] pb-4">
                <span className="flex items-center gap-3">
                    <OmnisightMark size={26} />
                    <span>
                        <span className="block text-[12.5px] text-[var(--muted)]">{org?.name}</span>
                        <span className="block text-[16px] font-semibold tracking-tight">Work-zone incident report</span>
                    </span>
                </span>
                <span className="ml-auto flex flex-wrap gap-7 text-right">
                    {/* The TMA number is how an incident is filed and talked about, so it heads the
                        report beside its reference rather than waiting for the Source card. */}
                    <span>
                        <span className="block text-[11px] text-[var(--muted)]">TMA</span>
                        <b className="text-[13.5px]">{siteLabel(device)}</b>
                    </span>
                    <span>
                        <span className="block text-[11px] text-[var(--muted)]">Report</span>
                        <b className="text-[13.5px] tabular-nums">EV-{(event.id ?? "").slice(0, 8).toUpperCase()}</b>
                    </span>
                    <span>
                        <span className="block text-[11px] text-[var(--muted)]">Generated</span>
                        <b className="text-[13.5px] tabular-nums">{new Date().toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</b>
                    </span>
                </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-[24px] font-bold tracking-tight">{CATEGORY_LABEL[category]}</h1>
                <Tag tone={tone}>{category === "collision" ? "Critical" : category === "speeding" ? "Warning" : "Informational"}</Tag>
                <span className="text-[13px] tabular-nums text-[var(--muted)]">{when ? when.toLocaleString() : ""}</span>
                {isReviewed(event) && (
                    <Tag tone={event.isValidAlert === false ? "mute" : "safe"}>
                        {event.isValidAlert === false ? "Reviewed · not a real alert" : "Reviewed · confirmed"}
                    </Tag>
                )}
                <span className="ml-auto flex gap-2 print:hidden">
                    <Btn tone="quiet" onClick={() => window.print()}>
                        Print report
                    </Btn>
                    <Btn onClick={() => setReviewOpen(true)}>
                        {isReviewed(event) ? "Update review" : "Mark reviewed"}
                    </Btn>
                </span>
            </div>

            {/* The reviewer's own words stay on the printed report — that is half the point of
                writing them down. */}
            {event.reviewNotes && (
                <Note>
                    <b className="font-semibold text-[var(--text)]">Reviewer's notes</b>
                    <span className="mt-1 block whitespace-pre-wrap">{event.reviewNotes}</span>
                </Note>
            )}

            {/* Target details */}
            <Card>
                <CardHead title="Target details" note={when?.toLocaleTimeString(undefined, { hour12: false })} />
                {/* No photo, no placeholder — the facts pane just takes the width. A giant "no photo"
                    box is a hole in a document someone may print and hand to a lawyer. */}
                <div className={`grid gap-5 p-5 ${photoUrl ? "lg:grid-cols-[minmax(0,400px)_minmax(0,1fr)]" : ""}`}>
                    {photoUrl && (
                        <img
                            src={photoUrl}
                            alt="Vehicle at the moment of the event"
                            className="w-full self-start overflow-hidden rounded-[12px] border border-[var(--line-2)]"
                        />
                    )}
                    <dl className="grid grid-cols-2 content-start gap-px overflow-hidden rounded-[12px] border border-[var(--line)] bg-[var(--line)] sm:grid-cols-3">
                        <Fact label="Maximum speed" value={facts.max} />
                        <Fact label="Average speed" value={facts.avg} />
                        <Fact label="Closest approach" value={facts.closest} />
                        {/* The near miss itself, in time. Sits beside "Closest approach" in feet:
                            the pair is how near the vehicle got, measured two ways. */}
                        {warningMs !== undefined && <Fact label="Closest to impact" value={formatMs(warningMs)} />}
                        <Fact label="Classification" value={facts.cls} mu />
                        <Fact label="Zone" value={zoneName ?? "Protected zone"} mu />
                        <Fact label="Track points" value={track.length ? String(track.length) : "—"} mu />
                    </dl>
                </div>
                {!hydrated && (
                    <p className="px-5 pb-4 text-[12px] text-[var(--faint)]">Loading the radar track…</p>
                )}
            </Card>

            {/*
              Clip.

              Two routes to the same footage, because there are two ways a clip comes to exist. A
              trigger that asked for video stamps the event with a clipAccessToken and the anonymous
              viewer endpoint serves it. A clip someone requested afterwards has no token at all —
              the server resolves that one by target window and hands back clipRequestId. Gating this
              card on the token alone is why the page said "No clip was pulled" for footage that was
              playing perfectly well on the main dashboard.
            */}
            <Card>
                {/* No "Not pulled" note: the pull card underneath owns that half of the story, and it can
                    change under a header that cannot — leaving "Not pulled" sitting above a clip that is
                    uploading. What stays is the verdict on a clip the *event* carries. */}
                <CardHead title="Clip" note={hasClip && deadClipNote ? "Unavailable" : undefined} />
                {hasClip ? (
                    clipVideoUrl ? (
                        <div className="p-5 pt-3">
                            <video src={clipVideoUrl} controls playsInline className="w-full overflow-hidden rounded-[12px] border border-[var(--line-2)] bg-black" />
                        </div>
                    ) : clipWatchUrl ? (
                        <div className="flex flex-wrap items-center gap-3 px-5 pb-5 pt-3">
                            <span className="text-[13px] text-[var(--muted)]">Footage for this event is ready.</span>
                            <a href={clipWatchUrl} target="_blank" rel="noreferrer" className="print:hidden">
                                <Btn>Watch clip</Btn>
                            </a>
                        </div>
                    ) : (
                        <p className="px-5 pb-5 pt-3 text-[13px] text-[var(--muted)]">
                            {event.clipAccessToken && !clip
                                ? "Loading clip…"
                                : (deadClipNote ??
                                  (clipPulled
                                      ? "The unit is still uploading this clip — check back shortly."
                                      : "This event asked for footage, but the unit hasn't sent the request yet."))}
                        </p>
                    )
                ) : (
                    <PullClip deviceId={event.deviceId} targetId={event.target?.id} hydrated={hydrated} reason={trackReason} />
                )}
            </Card>

            {/* Track: path + speed profile */}
            {track.length > 1 && (
                <div className="grid gap-4 lg:grid-cols-2">
                    <Card>
                        <CardHead title="Path taken" note={`Top-down · ${track.length} radar fixes`} />
                        <PathChart
                            track={track}
                            truckLabel={siteLabel(device) !== "—" ? siteLabel(device) : deviceName(event.deviceId)}
                            hoverIndex={hoverFix}
                            onHover={setHoverFix}
                        />
                    </Card>
                    <Card>
                        <CardHead title="Speed profile" note="Approach to closest point" />
                        {/* Same index space as the path: hovering either marks the fix in both. */}
                        <LineAreaChart
                            height={300}
                            labels={track.map((h, i) => `-${track.length - 1 - i}s`)}
                            series={[
                                {
                                    key: "spd",
                                    label: "Speed",
                                    color: "var(--accent)",
                                    area: true,
                                    values: track.map((h) => Math.round(Math.hypot(h.velocity?.x ?? 0, h.velocity?.y ?? 0, h.velocity?.z ?? 0) * MPS_TO_MPH)),
                                },
                            ]}
                            formatValue={(v) => `${v} mph`}
                            onHoverIndex={setHoverFix}
                            highlightIndex={hoverFix}
                        />
                    </Card>
                </div>
            )}

            {/* Target history table */}
            {track.length > 0 && (
                <Card>
                    <CardHead title="Target history" note="Radar track as recorded" />
                    <Table className="mt-2">
                            <thead>
                                <tr>
                                    <Th>Time</Th>
                                    <Th>Range</Th>
                                    <Th>Speed</Th>
                                    <Th>Closing rate</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {track.slice(-12).map((h, i) => {
                                    const range = Math.hypot(h.position?.x ?? 0, h.position?.y ?? 0);
                                    const speed = Math.hypot(h.velocity?.x ?? 0, h.velocity?.y ?? 0, h.velocity?.z ?? 0);
                                    // Radial component of velocity toward the sensor — the closing rate.
                                    const closing =
                                        range > 0
                                            ? -((h.position!.x * (h.velocity?.x ?? 0) + h.position!.y * (h.velocity?.y ?? 0)) / range)
                                            : 0;
                                    return (
                                        <tr key={h.id ?? i}>
                                            <Td label="Time" className="tabular-nums">
                                                {h.date ? new Date(h.date).toLocaleTimeString(undefined, { hour12: false }) : "—"}
                                            </Td>
                                            <Td label="Range" className="tabular-nums">{Math.round(range * M_TO_FT)} ft</Td>
                                            <Td label="Speed" className="tabular-nums">{Math.round(speed * MPS_TO_MPH)} mph</Td>
                                            <Td label="Closing rate" className="tabular-nums">{Math.round(closing * M_TO_FT)} ft/s</Td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                    </Table>
                </Card>
            )}

            {/* Where the path and speed profile would be: where the track is and whether to expect it.
                This used to say the server "no longer held" the track, which it never lets go of — the
                track simply hadn't arrived from the unit, and for a unit that dropped offline right
                after the event it still will. */}
            {trackReason && (
                <Note tone={trackReason === "offline" || trackReason === "uploading" ? "warn" : undefined}>{missingTrackNote(trackReason)}</Note>
            )}
            {hydrated && error && track.length === 0 && <Note tone="crit">Couldn't load the radar track for this event: {error}</Note>}

            {/* Source + response */}
            <div className="grid gap-4 lg:grid-cols-2">
                <Card>
                    <CardHead title="Source" />
                    <div className="mt-2">
                        <FRow title="Device">
                            {/* The obvious next question from a report is "what else has this unit
                                been doing?", and that is its own page. Only the arrow is dropped on
                                paper — the value itself prints exactly as it always did. */}
                            {device?.id ? (
                                <Link to={`/devices/${device.id}`} state={backToReport}>
                                    <FVal>
                                        {device.name}
                                        <span className="ml-1 print:hidden">→</span>
                                    </FVal>
                                </Link>
                            ) : (
                                <FVal>{deviceName(event.deviceId)}</FVal>
                            )}
                        </FRow>
                        <FRow title="Organization">
                            <FVal mu>{org?.name ?? "—"}</FVal>
                        </FRow>
                        <FRow title="Device key">
                            <FVal mu mono>
                                {device?.deviceKey ?? "—"}
                            </FVal>
                        </FRow>
                        <FRow title="Firmware">
                            <FVal mu>{device?.firmwareVersion ?? "—"}</FVal>
                        </FRow>
                    </div>
                </Card>
                <Card>
                    <CardHead title="Configured response" note="from the trigger that owns this zone">
                        {/* Straight to the alerts and relays this card is describing — reading that
                            a trigger fired one relay is usually the moment you want to change it. */}
                        {device?.id && (
                            <Link
                                to={`/devices/${device.id}/settings?tab=alerts`}
                                state={backToReport}
                                title="Alert settings for this unit"
                                aria-label="Alert settings for this unit"
                                className="grid h-7 w-7 place-items-center rounded-lg text-[var(--muted)] hover:bg-[var(--surface-2)] hover:text-[var(--text)] print:hidden"
                            >
                                <SettingsIcon size={16} />
                            </Link>
                        )}
                    </CardHead>
                    <div className="mt-2">
                        <FRow title="Recorded on device">
                            <FVal>{when ? when.toLocaleTimeString(undefined, { hour12: false }) : "—"}</FVal>
                        </FRow>
                        <FRow title="Relays on this trigger">
                            <FVal mu>{relayEndpoints.length > 0 ? relayEndpoints.map((r) => r.endpointName).join(" · ") : trigger ? "None" : "…"}</FVal>
                        </FRow>
                        <FRow
                            title="Notified"
                            sub={emailEndpoints.map((e) => (e as { emailAddress?: string }).emailAddress).join(", ") || undefined}
                        >
                            {emailEndpoints.length > 0 ? <Tag tone="safe">{emailEndpoints.length} recipient{emailEndpoints.length === 1 ? "" : "s"}</Tag> : <Tag tone="mute">No email endpoint</Tag>}
                        </FRow>
                    </div>
                    <div className="border-t border-[var(--line)] bg-[var(--surface-2)] px-5 py-3 text-[12px] leading-relaxed text-[var(--muted)]">
                        Times are the device clock, synchronised by NTP. This report reflects the record held on the server at the
                        moment it was generated.
                    </div>
                </Card>
            </div>

            <ReviewEventDialog
                open={reviewOpen}
                onClose={() => setReviewOpen(false)}
                event={event}
                orgId={eventOrgId ?? org?.id}
                onReviewed={(updated) => setEvent(updated)}
            />
        </div>
    );
};

/**
 * The clip nobody pulled yet — and the one click that pulls it.
 *
 * The window is the server's to decide, not this page's. Posting to the target endpoint has the
 * clip cut from the target's own tracking window plus five seconds either side, with the radar
 * data bundled in; a window computed here would be this app's guess at what the main dashboard
 * already does, and the two would drift. It is the same one-click route the dashboard's event
 * dialog takes, so a clip pulled from a report is identical to one pulled from there.
 *
 * The probe alongside it is what says whether asking is possible at all. It answers false for a
 * unit whose firmware predates media requests and for footage that has already aged off the DVR —
 * exactly the two cases where the request would come back as an error — so those are stated
 * instead of being offered as a button that cannot work.
 *
 * Nothing pushes here: the dashboard watches a SignalR hub for progress, but this console's rule
 * is that anything which ages polls, and it only polls while the unit is actually working.
 */
const PullClip: React.FC<{ deviceId?: string; targetId?: string; hydrated: boolean; reason?: MissingTrackReason }> = ({
    deviceId,
    targetId,
    hydrated,
    reason,
}) => {
    const toast = useToast();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();
    // useLoad takes its interval as an argument and the answer comes from what it fetches, so the
    // decision to keep polling is carried in state and settled after each response.
    const [pollMs, setPollMs] = useState<number>();

    const canAsk = Boolean(deviceId && targetId);
    const probe = useLoad(
        () => (deviceId && targetId ? MediaRequests.getTargetMediaRequest(deviceId, targetId) : Promise.resolve(undefined)),
        [deviceId, targetId],
        { pollMs },
    );

    const request = probe.data?.deviceMediaRequest;
    const working = isMediaRequestWorking(request);
    useEffect(() => setPollMs(working ? CLIP_POLL_MS : undefined), [working]);

    const ask = async () => {
        if (!deviceId || !targetId || busy) return;
        setBusy(true);
        setError(undefined);
        try {
            await MediaRequests.createRequestForTarget(deviceId, targetId);
            await probe.reload();
            toast("Clip requested — the unit uploads it on its next check-in");
        } catch (err) {
            // The server's own words beat ours here: it is the one that knows the firmware is too
            // old, that the footage has aged out, or that the window exceeds the seven-day cap.
            const message = err instanceof Error ? err.message.replace(/^\w*Error:\s*/, "") : "";
            setError(message || "Could not request that clip");
        } finally {
            setBusy(false);
        }
    };

    // The target is what the media endpoints take, and it only arrives with the hydrated event.
    if (!hydrated && !canAsk) {
        return <p className="px-5 pb-5 pt-3 text-[13px] text-[var(--muted)]">Loading…</p>;
    }

    // No target to ask about — a dwell or presence event, or a track the unit hasn't sent, which is
    // the report's own reading (missingTrack) and may yet change. The DVR page can still pull that
    // moment by hand, so it stays the way through.
    if (!canAsk) {
        return (
            <div className="flex flex-wrap items-center gap-3 px-5 pb-5 pt-3">
                <span className="text-[13px] text-[var(--muted)]">
                    {reason
                        ? missingTrackClipNote(reason)
                        : "The radar track for this event couldn't be loaded, so a clip can't be requested from here."}
                </span>
                <Link to="/media" className="print:hidden">
                    <Btn tone="quiet">Request by time instead</Btn>
                </Link>
            </div>
        );
    }

    // hasMediaRequest is the server's "this one can be pulled" — false for firmware that predates
    // media requests, and for a track older than the oldest footage the unit still holds.
    if (probe.data && !probe.data.hasMediaRequest) {
        return (
            <p className="px-5 pb-5 pt-3 text-[13px] text-[var(--muted)]">
                This unit can't supply footage for this event — either its recording no longer goes back this far, or its
                firmware predates clip requests.
            </p>
        );
    }

    const videoUrl = request?.videoUrl
        ? getApiUrl(`/api/mediarequest/${deviceId}/media-requests/${request.id}/video/view`)
        : undefined;
    const dead =
        request?.status === MediaRequestStatuses.Expired
            ? "That clip aged out of the unit's storage before it uploaded."
            : request?.status === MediaRequestStatuses.Cancelled
              ? "That request was cancelled before it uploaded."
              : request?.status === MediaRequestStatuses.Error
                ? "The unit could not produce that clip."
                : undefined;

    return (
        <div className="flex flex-col gap-2.5 px-5 pb-5 pt-3">
            <div className="flex flex-wrap items-center gap-3">
                {videoUrl ? (
                    <>
                        <span className="text-[13px] text-[var(--muted)]">The unit has uploaded this clip.</span>
                        <a href={videoUrl} target="_blank" rel="noreferrer" className="print:hidden">
                            <Btn>Watch clip</Btn>
                        </a>
                    </>
                ) : working ? (
                    <>
                        {request && <MediaRequestStatusTag request={request} />}
                        <span className="text-[13px] text-[var(--muted)]">
                            Asked this unit for the footage — it uploads on its next check-in.
                        </span>
                    </>
                ) : (
                    <>
                        <span className="text-[13px] text-[var(--muted)]">
                            {dead ?? "No clip was pulled for this event."}
                        </span>
                        <Btn tone="quiet" onClick={ask} disabled={busy || probe.isLoading} className="print:hidden">
                            {busy ? "Requesting…" : dead ? "Try again" : "Request from the device"}
                        </Btn>
                    </>
                )}
            </div>
            {/* The clip covers the pass itself: the server cuts it from the tracked window with five
                seconds of run-up and run-out, so there is nothing here to choose. */}
            {!videoUrl && !working && !dead && (
                <span className="text-[11.5px] text-[var(--faint)]">
                    Pulls the vehicle's pass with five seconds either side, plus the radar data.
                </span>
            )}
            {error && <p className="text-[12.5px] text-[var(--crit)]">{error}</p>}
        </div>
    );
};

const Fact: React.FC<{ label: string; value: string; mu?: boolean }> = ({ label, value, mu }) => (
    <div className="bg-[var(--surface)] px-4 py-4">
        <dt className="mb-1.5 text-[11.5px] text-[var(--muted)]">{label}</dt>
        <dd className={`m-0 tabular-nums tracking-tight ${mu ? "text-[15px] font-semibold text-[var(--muted)]" : "text-[19px] font-semibold"}`}>{value}</dd>
    </div>
);

/** Top-down plot of the recorded track: the truck at the origin, the road running away from it. */
/**
 * The track from above, with every fix hoverable.
 *
 * Indices here are the same indices the speed profile beside it uses — the page owns the hovered
 * one — so pointing at a moment in either chart marks it in both. That pairing is the point: the
 * speed profile says how fast, the path says where, and a near miss is only legible as both.
 *
 * Dots are thinned on a long track to keep it readable, but the hit-testing runs over every fix,
 * so a point with no dot drawn still answers when you point at it and still takes the ring when
 * the speed chart calls for it.
 */
const PathChart: React.FC<{
    track: TargetHistory[];
    truckLabel: string;
    hoverIndex?: number;
    onHover?: (index: number | undefined) => void;
}> = ({ track, truckLabel, hoverIndex, onHover }) => {
    const W = 340;
    const H = 300;
    const wrapRef = useRef<HTMLDivElement>(null);
    const xs = track.map((h) => h.position?.x ?? 0);
    const ys = track.map((h) => h.position?.y ?? 0);
    const maxAbsX = Math.max(6, ...xs.map(Math.abs));
    const maxY = Math.max(10, ...ys.map(Math.abs));
    // Truck near the bottom; downrange grows upward.
    const px = (x: number) => W / 2 + (x / maxAbsX) * (W / 2 - 40);
    const py = (y: number) => H - 55 - (Math.abs(y) / maxY) * (H - 90);

    const step = track.length > 14 ? Math.ceil(track.length / 14) : 1;
    const isDrawn = (i: number) => i % step === 0 || i === track.length - 1;

    const active = hoverIndex !== undefined ? track[hoverIndex] : undefined;

    // Nearest fix to the pointer, in viewBox space — closest by straight-line distance rather than
    // by x alone, since a path can double back on itself.
    const onMove = (e: React.MouseEvent<SVGRectElement>) => {
        const svg = (e.target as SVGRectElement).ownerSVGElement!;
        const r = svg.getBoundingClientRect();
        const mx = ((e.clientX - r.left) / r.width) * W;
        const my = ((e.clientY - r.top) / r.height) * H;
        let best = 0;
        let bestDist = Infinity;
        track.forEach((h, i) => {
            const d = Math.hypot(px(h.position?.x ?? 0) - mx, py(h.position?.y ?? 0) - my);
            if (d < bestDist) {
                bestDist = d;
                best = i;
            }
        });
        onHover?.(best);
    };

    return (
        <div ref={wrapRef} className="relative px-4 pb-4 pt-2">
            <svg viewBox={`0 0 ${W} ${H}`} className="block h-auto w-full">
                <rect width={W} height={H} rx={10} fill="var(--surface-2)" />
                {/* Truck + protected zone at the origin */}
                <rect x={W / 2 - 24} y={H - 50} width={48} height={38} rx={6} fill="var(--surface)" stroke="var(--line-2)" />
                <rect x={W / 2 - 28} y={H - 58} width={56} height={9} rx={2} fill="var(--warn)" />
                <text x={W / 2} y={H - 26} textAnchor="middle" fontSize={12} fill="var(--muted)">
                    {truckLabel}
                </text>
                <path
                    d={track.map((h, i) => `${i ? "L" : "M"}${px(h.position?.x ?? 0)},${py(h.position?.y ?? 0)}`).join(" ")}
                    fill="none"
                    stroke="var(--crit)"
                    strokeWidth={2.4}
                    strokeDasharray="6 5"
                />
                {track.map((h, i) =>
                    isDrawn(i) ? (
                        <circle
                            key={i}
                            cx={px(h.position?.x ?? 0)}
                            cy={py(h.position?.y ?? 0)}
                            r={i === track.length - 1 ? 6 : 3.4}
                            fill={i === track.length - 1 ? "var(--crit)" : "var(--warn)"}
                            stroke="var(--surface-2)"
                            strokeWidth={1.5}
                        />
                    ) : null,
                )}
                {active && (
                    <g>
                        <circle cx={px(active.position?.x ?? 0)} cy={py(active.position?.y ?? 0)} r={11} fill="var(--accent)" opacity={0.25} />
                        <circle
                            cx={px(active.position?.x ?? 0)}
                            cy={py(active.position?.y ?? 0)}
                            r={5.5}
                            fill="var(--accent)"
                            stroke="var(--surface)"
                            strokeWidth={2}
                        />
                    </g>
                )}
                <rect
                    width={W}
                    height={H}
                    fill="transparent"
                    className="cursor-crosshair"
                    onMouseMove={onMove}
                    onMouseLeave={() => onHover?.(undefined)}
                />
            </svg>

            {active && (
                <TrackTip
                    fix={active}
                    index={hoverIndex!}
                    total={track.length}
                    leftPct={(px(active.position?.x ?? 0) / W) * 100}
                    topPct={(py(active.position?.y ?? 0) / H) * 100}
                />
            )}
        </div>
    );
};

/**
 * Everything the server actually holds about one radar fix.
 *
 * Point count is the raw detection density behind the fix, and closing rate is the radial
 * component of the tracked velocity — the nearest thing to a Doppler reading that survives the
 * trip to the cloud. Per-point SNR and raw Doppler bins are computed on the device (the radar
 * classifier's own inputs) and are not uploaded, so there is nothing to show for them here.
 */
const TrackTip: React.FC<{ fix: TargetHistory; index: number; total: number; leftPct: number; topPct: number }> = ({
    fix,
    index,
    total,
    leftPct,
    topPct,
}) => {
    const range = Math.hypot(fix.position?.x ?? 0, fix.position?.y ?? 0);
    const speed = Math.hypot(fix.velocity?.x ?? 0, fix.velocity?.y ?? 0, fix.velocity?.z ?? 0);
    const closing = range > 0 ? -(((fix.position?.x ?? 0) * (fix.velocity?.x ?? 0) + (fix.position?.y ?? 0) * (fix.velocity?.y ?? 0)) / range) : 0;
    const size = fix.width > 0 || fix.height > 0 ? `${(fix.width * M_TO_FT).toFixed(1)} × ${(fix.height * M_TO_FT).toFixed(1)} ft` : undefined;

    // Turn to face inward near the edges, the same rule the cartesian charts follow.
    const align = leftPct < 32 ? "0" : leftPct > 68 ? "-100%" : "-50%";
    const below = topPct < 34;

    return (
        <div
            className="pointer-events-none absolute z-10 whitespace-nowrap rounded-[11px] border border-[var(--line-2)] bg-[var(--surface-3)] px-3.5 py-2.5 shadow-[0_16px_40px_rgba(0,0,0,.45)]"
            style={{
                left: `calc(${leftPct}% + 1rem)`,
                top: `calc(${topPct}% + ${below ? "1.5rem" : "0rem"})`,
                transform: `translate(${align}, ${below ? "0" : "calc(-100% - 0.75rem)"})`,
            }}
        >
            <TipTitle>
                {fix.date ? new Date(fix.date).toLocaleTimeString(undefined, { hour12: false }) : `Fix ${index + 1}`}
                <span className="ml-2 font-normal text-[var(--faint)]">
                    {index + 1} of {total}
                </span>
            </TipTitle>
            <TipRow label="Speed" value={`${Math.round(speed * MPS_TO_MPH)} mph`} />
            <TipRow label="Closing rate" value={`${Math.round(closing * M_TO_FT)} ft/s`} />
            <TipRow label="Range" value={`${Math.round(range * M_TO_FT)} ft`} />
            <TipRow label="Radar points" value={fix.pointCount > 0 ? String(fix.pointCount) : "—"} />
            {size && <TipRow label="Size" value={size} />}
            <TipRow label="Class" value={getTargetTypeDisplayName(fix.type) ?? "—"} />
        </div>
    );
};
